// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useMemo } from 'react';
import { formatDateShort } from '@loa/theme';
import { Alert, AppIcon, Spinner } from '@loa/ui-components';
import type { PolicyDocumentVersionInfo } from '../types/policyDocument';
import { getFileIcon } from '../types/policyDocument';
import type { ProviderSubmissionDTO } from '../types/providerAccess';
import type { ClientUploadedDocumentDTO } from '../api/clientDocumentApi';
import { useTags } from '../contexts/TagsContext';
import { useEntityTags } from '../hooks/useEntityTags';
import { EntityTagEditor } from './tags/EntityTagEditor';
import './PolicyDocumentsTable.css';

/** Unified row for advisor versions, provider submissions, and client uploads */
export interface DocumentRow {
  id: string;
  filename: string;
  contentType: string;
  uploadedAt: string;
  uploadedBy: string;
  source: 'advisor' | 'provider' | 'client-upload';
  /** For advisor docs: version number for download */
  version?: number;
  /** For provider docs: submission ID for download */
  submissionId?: string;
  /** For client uploads: client_documents id for download-by-id */
  documentId?: string;
}

type PolicyDocumentsTableProps = Readonly<{
  versions: PolicyDocumentVersionInfo[];
  providerDocuments: ProviderSubmissionDTO[];
  /** Documents the client uploaded and linked to this policy. */
  clientUploads?: ClientUploadedDocumentDTO[];
  loading: boolean;
  error: string | null;
  onDownloadVersion: (version: number) => Promise<void> | void;
  onDownloadProviderDoc: (submissionId: string, filename: string) => Promise<void> | void;
  /** Download a client upload by its client_documents id. */
  onDownloadClientUpload?: (documentId: string, filename: string) => Promise<void> | void;
  onViewDocument: (row: DocumentRow) => void;
  onUploadClick: () => void;
  currentAdvisorName?: string;
  currentAdvisorKeycloakId?: string;
}>;

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function PolicyDocumentsTable({
  versions,
  providerDocuments,
  clientUploads = [],
  loading,
  error,
  onDownloadVersion,
  onDownloadProviderDoc,
  onDownloadClientUpload,
  onViewDocument,
  onUploadClick,
  currentAdvisorName,
  currentAdvisorKeycloakId,
}: PolicyDocumentsTableProps) {
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  /** Resolve UUID to display name for legacy data that stored Keycloak subject IDs */
  const resolveUserDisplay = (value: string): string => {
    if (!value) return 'Unknown';
    if (value === 'Provider' || value === 'System') return value;
    if (UUID_REGEX.test(value) && currentAdvisorKeycloakId && value === currentAdvisorKeycloakId && currentAdvisorName) {
      return currentAdvisorName;
    }
    return value;
  };

  /** Format filename with uppercase extension: "report.pdf" → "report.PDF" */
  const formatFilename = (filename: string): string => {
    const dotIndex = filename.lastIndexOf('.');
    if (dotIndex === -1) return filename;
    return filename.slice(0, dotIndex + 1) + filename.slice(dotIndex + 1).toUpperCase();
  };

  // Merge advisor versions + provider document submissions into a unified list
  const rows: DocumentRow[] = useMemo(() => {
    const advisorRows: DocumentRow[] = versions.map((v) => ({
      id: `v-${v.version}`,
      filename: v.filename,
      contentType: v.contentType,
      uploadedAt: v.uploadedAt,
      uploadedBy: v.uploadedBy,
      source: 'advisor' as const,
      version: v.version,
    }));

    const providerRows: DocumentRow[] = providerDocuments
      .filter((s) => s.submissionType === 'DOCUMENT' && s.documentFilename)
      .map((s) => ({
        id: `ps-${s.id}`,
        filename: s.documentFilename!,
        contentType: s.documentContentType || 'application/octet-stream',
        uploadedAt: s.submittedAt,
        uploadedBy: 'Provider',
        source: 'provider' as const,
        submissionId: s.id,
      }));

    // Client-uploaded documents linked to this policy (Ahmed's rule: a document
    // the client says relates to a policy lands on the policy's Documents tab).
    const clientUploadRows: DocumentRow[] = clientUploads.map((u) => ({
      id: `cu-${u.id}`,
      filename: u.documentName || u.originalFilename,
      contentType: u.contentType || 'application/octet-stream',
      uploadedAt: u.uploadedAt,
      uploadedBy: u.uploadedBy,
      source: 'client-upload' as const,
      documentId: u.id,
    }));

    // Sort all docs by date descending
    return [...advisorRows, ...providerRows, ...clientUploadRows].sort(
      (a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime()
    );
  }, [versions, providerDocuments, clientUploads]);

  // Tags column (Tags PRD §9, DOCUMENT entity type) — only client-upload
  // rows carry a real `client_documents.id` (`documentId`); advisor
  // versions and provider submissions are not `client_documents` rows and
  // are not taggable (see the Phase 3 report's documented limitation for
  // this mixed-source surface).
  const { featureEnabled: tagsFeatureEnabled } = useTags();
  const clientUploadDocumentIds = clientUploads.map((u) => u.id);
  const { tagsByEntityId } = useEntityTags('DOCUMENT', clientUploadDocumentIds);

  const handleDownload = async (row: DocumentRow) => {
    setDownloadingId(row.id);
    try {
      if (row.source === 'advisor' && row.version != null) {
        await onDownloadVersion(row.version);
      } else if (row.source === 'provider' && row.submissionId) {
        await onDownloadProviderDoc(row.submissionId, row.filename);
      } else if (row.source === 'client-upload' && row.documentId && onDownloadClientUpload) {
        await onDownloadClientUpload(row.documentId, row.filename);
      }
    } finally {
      setDownloadingId(null);
    }
  };

  if (loading) {
    return (
      <div className="documents-table-card">
        <div className="documents-table-card__header">
          <h3 className="documents-table-card__title">Documents</h3>
        </div>
        <div className="documents-table-card__loading">
          <Spinner />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="documents-table-card">
        <div className="documents-table-card__header">
          <h3 className="documents-table-card__title">Documents</h3>
        </div>
        <Alert variant="error" header="Failed to Load Documents">
          <p>{error}</p>
        </Alert>
      </div>
    );
  }

  return (
    <div className="documents-table-card">
      <div className="documents-table-card__header">
        <h3 className="documents-table-card__title">Documents</h3>
        <button
          type="button"
          className="documents-table-card__upload-btn"
          onClick={onUploadClick}
        >
          Upload document
        </button>
      </div>

      {rows.length === 0 ? (
        <div className="documents-table-card__empty">
          <span className="documents-table-card__empty-text">No documents available</span>
        </div>
      ) : (
        <table className="documents-table">
          <thead>
            <tr>
              <th>Document name</th>
              <th>Date uploaded</th>
              <th>Uploaded by</th>
              {tagsFeatureEnabled && <th>Tags</th>}
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td className="documents-table__name-cell">
                  <AppIcon name={getFileIcon(row.contentType) as 'file'} />
                  <span className="documents-table__filename">{formatFilename(row.filename)}</span>
                </td>
                <td>{formatDateShort(row.uploadedAt)}</td>
                <td>{resolveUserDisplay(row.uploadedBy)}</td>
                {tagsFeatureEnabled && (
                  <td>
                    {row.source === 'client-upload' && row.documentId ? (
                      <EntityTagEditor
                        entityType="DOCUMENT"
                        entityId={row.documentId}
                        tags={tagsByEntityId[row.documentId] ?? []}
                      />
                    ) : null}
                  </td>
                )}
                <td className="documents-table__action-cell">
                  <button
                    type="button"
                    className="documents-table__action-btn"
                    title="View document"
                    aria-label={`View ${row.filename}`}
                    onClick={() => onViewDocument(row)}
                  >
                    <AppIcon name="eye" size={16} color="#2D0A6C" />
                  </button>
                  <button
                    type="button"
                    className="documents-table__action-btn"
                    disabled={downloadingId !== null}
                    title="Download"
                    aria-label={`Download ${row.filename}`}
                    onClick={() => handleDownload(row)}
                  >
                    {downloadingId === row.id ? (
                      <span className="documents-table__action-spinner" />
                    ) : (
                      <AppIcon name="download" size={16} color="#2D0A6C" />
                    )}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
